import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLang } from "../i18n";
import { openSocialShare } from "../lib/share";
import { toast } from "sonner";
import { Share2, X, MessageCircle, Instagram, Music2, Link2, Copy } from "lucide-react";

// Logo ufficiale di X (lucide non include le icone brand)
const XIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

export function ShareFab() {
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  const url = window.location.origin;
  const text = t("share.site_text");
  const full = `${text}\n${url}`;
  const openingMsg = (label) => t("share.opening", { social: label });

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success(t("share.link_copied"));
    } catch {
      toast.error("Copia non riuscita: seleziona e copia manualmente il link.");
    }
  };
  const openLink = (u) => window.open(u, "_blank", "noopener,noreferrer");

  const socials_list = [
    { id: "whatsapp", label: "WhatsApp", icon: MessageCircle, color: "#25D366", onClick: () => openLink(`https://wa.me/?text=${encodeURIComponent(full)}`) },
    { id: "x", label: "X", icon: XIcon, color: "#F5F5F5", onClick: () => openLink(`https://twitter.com/intent/tweet?text=${encodeURIComponent(full)}`) },
    { id: "instagram", label: "Instagram", icon: Instagram, color: "#E1306C", onClick: () => openSocialShare("instagram", { full, openingMsg, siteUrl: url }) },
    { id: "tiktok", label: "TikTok", icon: Music2, color: "#FE2C55", onClick: () => openSocialShare("tiktok", { full, openingMsg, siteUrl: url }) },
    { id: "discord", label: "Discord", icon: Link2, color: "#5865F2", onClick: () => openSocialShare("discord", { full, openingMsg }) },
    { id: "copylink", label: t("share.copy_link"), icon: Copy, color: "#C9C9CE", onClick: copyLink },
  ];

  return (
    <div className="fixed bottom-5 right-5 z-50 flex flex-col items-end gap-3" data-testid="share-fab">
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 12, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 12, scale: 0.96 }}
            transition={{ duration: 0.2 }}
            className="chamfer-sm border border-[#1A1A1A] bg-[#0C0C0C]/95 backdrop-blur-md p-2.5 flex flex-col gap-1 w-52"
            data-testid="share-fab-panel"
          >
            {socials_list.map((s) => (
              <button
                key={s.id}
                onClick={s.onClick}
                data-testid={`share-fab-${s.id}`}
                className="flex items-center gap-3 px-3 py-2.5 font-mono text-[11px] uppercase tracking-[0.15em] font-bold hover:bg-[#1A1A1A] transition-colors duration-200 text-left"
                style={{ color: s.color }}
              >
                <s.icon className="w-4 h-4 shrink-0" /> {s.label}
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
      <button
        onClick={() => setOpen(!open)}
        data-testid="share-fab-button"
        aria-label={t("gv.share")}
        className="chamfer-sm bg-wardogs hover:bg-wardogs-hover text-[#050505] font-mono text-[11px] uppercase tracking-[0.2em] font-bold px-5 py-3 flex items-center gap-2 transition-all duration-300"
        style={{ boxShadow: "0 0 24px rgba(255,92,0,0.35)" }}
      >
        {open ? <X className="w-4 h-4" /> : <Share2 className="w-4 h-4" />} {t("gv.share")}
      </button>
    </div>
  );
}
